import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Zap, TrendingDown, Upload, Send, Phone, Mail, User, Building, FileText, Calendar, Clock, Shield, CheckCircle2, ArrowLeft, X, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

const Anfrage = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'fastlane';
  const isFastLane = type === 'fastlane';
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    description: '',
    quantity: '',
    deadline: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50 MB gesamt
  const MAX_FILES = 10;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
    // reset input so gleicher Dateiname erneut ausgewählt werden kann
    e.target.value = '';
  };

  const addFiles = (incoming: File[]) => {
    setFiles(prev => {
      const existingKeys = new Set(prev.map(f => `${f.name}_${f.size}`));
      const deduped = incoming.filter(f => !existingKeys.has(`${f.name}_${f.size}`));
      const combined = [...prev, ...deduped];

      if (combined.length > MAX_FILES) {
        toast({
          title: "Zu viele Dateien",
          description: `Maximal ${MAX_FILES} Dateien pro Anfrage.`,
          variant: "destructive",
        });
        return prev;
      }

      const totalSize = combined.reduce((s, f) => s + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        toast({
          title: "Gesamtgröße überschritten",
          description: "Alle Dateien zusammen dürfen max. 50 MB groß sein.",
          variant: "destructive",
        });
        return prev;
      }

      return combined;
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('company', formData.company);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('description', formData.description);
      body.append('quantity', formData.quantity);
      body.append('deadline', formData.deadline);
      body.append('type', type);
      files.forEach(f => body.append('files', f));

      const response = await fetch('/api/anfrage', {
        method: 'POST',
        body,
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Der Server ist nicht erreichbar. Bitte versuchen Sie es später erneut.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Anfrage konnte nicht gesendet werden.');
      }

      toast({
        title: isFastLane ? "FastLane Anfrage gesendet!" : "Anfrage gesendet!",
        description: isFastLane 
          ? "Wir melden uns in wenigen Minuten bei Ihnen." 
          : "Wir erstellen Ihnen ein kostenoptimiertes Angebot.",
      });

      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        description: '',
        quantity: '',
        deadline: '',
      });
      setFiles([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten.';
      toast({
        title: "Fehler beim Senden",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = isFastLane
    ? [
        { icon: Clock, text: 'Antwort in wenigen Minuten' },
        { icon: Zap, text: 'Fertigung noch heute möglich' },
        { icon: Phone, text: 'Direkter Experten-Kontakt' },
      ]
    : [
        { icon: TrendingDown, text: 'Bis zu 40% Kostenersparnis' },
        { icon: Calendar, text: 'Flexible Terminplanung' },
        { icon: Shield, text: 'Volle Qualitätsgarantie' },
      ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={
          isFastLane
            ? 'Express-Anfrage CNC-Fertigung | SR-Zerspanung'
            : 'Kostenoptimierte Anfrage CNC-Fertigung | SR-Zerspanung'
        }
        description={
          isFastLane
            ? 'FastLane-Anfrage für eilige CNC-Teile: Zeichnung hochladen, Antwort in Minuten. 3- und 5-Achs Fräsen aus Stamsried.'
            : 'Kostenoptimierte CNC-Anfrage: Zeichnung hochladen und bis zu 40% sparen durch intelligente Kapazitätsplanung. Fräsen aus Stamsried.'
        }
        path="/anfrage"
      />
      <Header />
      
      <main className="pt-28 md:pt-32">
        {/* Hero Section with Gradient */}
        <div className={`relative overflow-hidden ${isFastLane ? 'bg-gradient-fastlane' : 'bg-gradient-smart'}`}>
          <div className="absolute inset-0 pattern-blueprint opacity-10" />
          
          <div className="container mx-auto px-4 py-24 lg:py-40 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Back Link */}
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-8 group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Zurück zur Startseite
              </Link>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  {isFastLane ? (
                    <Zap className="w-7 h-7 text-white" />
                  ) : (
                    <TrendingDown className="w-7 h-7 text-white" />
                  )}
                </div>
                <div className="h-px flex-1 bg-white/20 max-w-[100px]" />
              </div>

              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4">
                {isFastLane ? 'Express-Fertigung' : 'Kostenoptimiert'}
              </h1>
              <p className="text-white/80 text-lg md:text-xl max-w-xl">
                {isFastLane 
                  ? 'Schnelle Reaktionszeit. Sofortige Bearbeitung. Experten-Support.'
                  : 'Intelligente Planung. Maximale Ersparnis. Gleiche Qualität.'}
              </p>

              {/* Benefits Pills */}
              <div className="flex flex-wrap gap-3 mt-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div
                      key={benefit.text}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2"
                    >
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-white text-sm font-medium">{benefit.text}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Decorative Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" className="w-full">
              <path 
                d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                fill="hsl(var(--background))"
              />
            </svg>
          </div>
        </div>

        {/* Form Section */}
        <div className="container mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Sidebar Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Quick Contact */}
              {isFastLane && (
                <div className="bg-gradient-to-br from-fastlane/10 to-fastlane/5 rounded-2xl p-6 border border-fastlane/20">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2">
                    Lieber telefonieren?
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Unser Team ist sofort für Sie da.
                  </p>
                  <Button 
                    variant="fastlane" 
                    className="w-full gap-2"
                    asChild
                  >
                    <a href="tel:+4994669115130">
                      <Phone className="w-4 h-4" />
                      09466 / 911 51 30
                    </a>
                  </Button>
                  <p className="text-center text-xs text-muted-foreground mt-3">
                    Mo-Fr 7:00-16:00 Uhr
                  </p>
                </div>
              )}

              {/* Process Info */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-lg text-foreground">
                  So geht's weiter:
                </h3>
                <div className="space-y-3">
                  {[
                    'Anfrage absenden',
                    isFastLane ? 'Rückruf in Minuten' : 'Angebot in 24-48h',
                    'Auftrag bestätigen',
                    'Fertigung & Lieferung',
                  ].map((step, index) => (
                    <div key={step} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isFastLane 
                          ? 'bg-fastlane/10 text-fastlane' 
                          : 'bg-smart/10 text-smart'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="text-foreground text-sm">{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Badge */}
              <div className="bg-card rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="font-heading font-bold text-foreground">Ihre Daten sind sicher</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Alle Daten werden verschlüsselt übertragen und vertraulich behandelt.
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="bg-card rounded-3xl p-6 sm:p-8 lg:p-10 border border-border shadow-xl">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Section: Contact */}
                  <div>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isFastLane ? 'bg-fastlane/10' : 'bg-smart/10'
                      }`}>
                        <User className={`w-4 h-4 ${isFastLane ? 'text-fastlane' : 'text-smart'}`} />
                      </div>
                      Kontaktdaten
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground font-medium">
                          Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Max Mustermann"
                          required
                          maxLength={100}
                          className="h-12 rounded-xl bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-foreground font-medium">
                          Firma
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Musterfirma GmbH"
                          maxLength={100}
                          className="h-12 rounded-xl bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground font-medium">
                          E-Mail <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="max@musterfirma.de"
                          required
                          maxLength={255}
                          className="h-12 rounded-xl bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-foreground font-medium">
                          Telefon {isFastLane && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+49 123 456 789"
                          required={isFastLane}
                          maxLength={30}
                          className="h-12 rounded-xl bg-background"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-border" />

                  {/* Section: Project */}
                  <div>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isFastLane ? 'bg-fastlane/10' : 'bg-smart/10'
                      }`}>
                        <FileText className={`w-4 h-4 ${isFastLane ? 'text-fastlane' : 'text-smart'}`} />
                      </div>
                      Projektdetails
                    </h3>
                    
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-foreground font-medium">
                          Beschreibung <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="Material, Abmessungen, Toleranzen, besondere Anforderungen..."
                          rows={5}
                          required
                          maxLength={2000}
                          className="rounded-xl bg-background resize-none"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="quantity" className="text-foreground font-medium">
                            Stückzahl
                          </Label>
                          <Input
                            id="quantity"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            placeholder="z.B. 50 Stück"
                            maxLength={50}
                            className="h-12 rounded-xl bg-background"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="deadline" className="text-foreground font-medium">
                            {isFastLane ? 'Benötigt bis' : 'Wunschtermin'}
                          </Label>
                          <Input
                            id="deadline"
                            name="deadline"
                            type="date"
                            value={formData.deadline}
                            onChange={handleInputChange}
                            className="h-12 rounded-xl bg-background"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-border" />

                  {/* Section: File Upload */}
                  <div>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-6 flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isFastLane ? 'bg-fastlane/10' : 'bg-smart/10'
                      }`}>
                        <Upload className={`w-4 h-4 ${isFastLane ? 'text-fastlane' : 'text-smart'}`} />
                      </div>
                      CAD-Daten, Zeichnung, 3D-Modell (optional)
                    </h3>
                    
                    {files.length > 0 && (
                      <ul className="mb-4 space-y-2">
                        {files.map((f, index) => (
                          <li
                            key={`${f.name}_${f.size}_${index}`}
                            className="flex items-center gap-3 bg-muted/40 border border-border rounded-xl px-4 py-3"
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              isFastLane ? 'bg-fastlane/10' : 'bg-smart/10'
                            }`}>
                              <Paperclip className={`w-4 h-4 ${isFastLane ? 'text-fastlane' : 'text-smart'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground text-sm truncate">{f.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {(f.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                              aria-label={`${f.name} entfernen`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                        <li className="text-xs text-muted-foreground text-right pr-1">
                          Gesamt: {(files.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(2)} MB
                          {' '}von 50 MB · {files.length}/{MAX_FILES} Dateien
                        </li>
                      </ul>
                    )}

                    <div
                      className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                        dragOver
                          ? `border-${isFastLane ? 'fastlane' : 'smart'} bg-${isFastLane ? 'fastlane' : 'smart'}/5`
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="file"
                        multiple
                        accept=".step,.stp,.iges,.igs,.stl,.dxf,.dwg,.pdf,.zip,.rar,.7z"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-3">
                        <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${
                          isFastLane ? 'bg-fastlane/10' : 'bg-smart/10'
                        }`}>
                          <Upload className={`w-7 h-7 ${isFastLane ? 'text-fastlane' : 'text-smart'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {files.length > 0 ? 'Weitere Dateien hinzufügen' : 'Dateien hierher ziehen'}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            oder <span className={isFastLane ? 'text-fastlane' : 'text-smart'}>durchsuchen</span>
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          STEP, STP, IGES, STL, DXF, DWG, PDF, ZIP, RAR, 7Z · Max. 50 MB gesamt · bis zu {MAX_FILES} Dateien
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="pt-4">
                    <Button
                      type="submit"
                      variant={isFastLane ? 'fastlane' : 'default'}
                      size="xl"
                      className="w-full h-14 text-lg rounded-xl gap-3"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {isFastLane ? 'FastLane Anfrage senden' : 'Kostenoptimierte Anfrage senden'}
                        </>
                      )}
                    </Button>
                    
                    <p className="text-center text-xs text-muted-foreground mt-4">
                      Mit dem Absenden stimmen Sie unserer{' '}
                      <Link to="/datenschutz" className="text-primary hover:underline">
                        Datenschutzerklärung
                      </Link>{' '}
                      zu.
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Anfrage;
